import React, { useEffect, useState } from 'react';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import { SearchRateTablesResponseDto } from '../../redux/api/rateTable/rateTable.model';
import DateUtils from '../../utils/dateUtils';
import { Box, IconButton, SxProps } from '@mui/material';
import TwTooltip from '../TwTooltip/TwTooltip';
import AttachmentIcon from '@mui/icons-material/Attachment';
import TwDocumentViewer from '../TwDocumentViewer/TwDocumentViewer';
import { AttachmentFile } from '../../redux/api/otherExpenseAttachment/otherExpenseAttachment.model';

interface TwRateTableSelectorProps {
  data: SearchRateTablesResponseDto[];
  value?: string | null;
  customerId?: string;
  sx?: SxProps;
  onRateTableSelected: (rateTableId: string) => void;
}

export const TwRateTableSelector: React.FC<TwRateTableSelectorProps> = ({
  data,
  value,
  customerId,
  sx = {},
  onRateTableSelected
}) => {
  const [rateTableDocument, setRateTableDocument] = useState<AttachmentFile | null>(null);
  const [docViewOpen, setDocViewOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>(undefined);
  const [rateTables, setRateTables] = useState<TwAutocompleteOption[]>([]);
  const [rateTableSelectedValue, setRateTableSelectedValue] = React.useState<
    TwAutocompleteOption | undefined
  >(undefined);

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TwAutocompleteOption | null
  ): void => {
    const rateTableId = value?.id;

    const rateTable = data.find((rateTable) => rateTable.id === rateTableId);

    if (rateTable) {
      const doc = rateTable.document;
      if (doc) {
        setRateTableDocument(doc);
        setSelectedDocumentId(doc.id);
      } else {
        setRateTableDocument(null);
        setSelectedDocumentId(undefined);
      }
      onRateTableSelected(rateTable.id);
    } else {
      onRateTableSelected('');
    }
  };

  const transformResponse = (rateTables: SearchRateTablesResponseDto[]): TwAutocompleteOption[] => {
    return rateTables.map((rateTable) => ({
      id: rateTable.id,
      label: rateTable.name,
      subLabel: `${DateUtils.getMediumDateInLocalTimeZone(new Date(rateTable.start_date))} - ${
        rateTable.end_date
          ? DateUtils.getMediumDateInLocalTimeZone(new Date(rateTable.end_date))
          : 'Current'
      }`,
      value: rateTable.id,
      endAdornmentIcon: rateTable.document_id ? (
        <AttachmentIcon
          sx={{
            color: '#7E858E',
            transform: 'rotate(-40deg)',
            opacity: '50%'
          }}
        />
      ) : undefined
    }));
  };

  const transformValueToOption = (value: string): TwAutocompleteOption | undefined => {
    if (!value) {
      return {
        id: '',
        label: 'Select a rate table',
        subLabel: '',
        value: ''
      };
    }

    const rateTable = data.find((rateTable) => rateTable.id === value);
    if (!rateTable) {
      return {
        id: '',
        label: 'Select a rate table',
        subLabel: '',
        value: ''
      };
    }

    return {
      id: rateTable.id,
      label: rateTable.name,
      subLabel: `${DateUtils.getMediumDateInLocalTimeZone(new Date(rateTable.start_date))} - ${
        rateTable.end_date
          ? DateUtils.getMediumDateInLocalTimeZone(new Date(rateTable.end_date))
          : 'Current'
      }`,
      value: rateTable.id,
      endAdornmentIcon: rateTable.document_id ? (
        <AttachmentIcon
          sx={{
            color: '#7E858E',
            transform: 'rotate(-40deg)',
            opacity: '50%'
          }}
        />
      ) : undefined
    };
  };

  const handleSetRateTableDocument = (rateTableId: string): void => {
    const rateTable = data.find((rateTable) => rateTable.id === rateTableId);

    if (rateTable) {
      const doc = rateTable.document;
      if (doc) {
        setRateTableDocument(doc);
        setSelectedDocumentId(doc.id);
        setDocViewOpen(true);
      } else {
        setRateTableDocument(null);
        setSelectedDocumentId(undefined);
        setDocViewOpen(false);
      }
    } else {
      setRateTableDocument(null);
    }
  };

  useEffect(() => {
    setRateTableSelectedValue(transformValueToOption(value || ''));
  }, [value]);

  useEffect(() => {
    if (data.length > 0) {
      setRateTables(transformResponse(data));
    }
  }, [data]);

  return (
    <Box sx={{ width: '100%', display: 'inline-flex', flexDirection: 'row' }}>
      <TwAutocomplete
        name="rate_table"
        label="Rate table"
        optionList={rateTables}
        value={rateTableSelectedValue || transformValueToOption(value || '')}
        onChange={handleChange}
        sx={{
          ...sx,
          display: 'inline-flex'
        }}
        disabled={!customerId}
      />
      <Box
        sx={{
          display: rateTableSelectedValue?.endAdornmentIcon ? 'inline-flex' : 'none',
          justifyContent: 'flex-end',
          mt: 2,
          p: 1
        }}
      >
        <IconButton
          title="View rate table document"
          onClick={(): void => handleSetRateTableDocument(rateTableSelectedValue?.id || '')}
        >
          <AttachmentIcon
            sx={{
              color: '#7E858E',
              transform: 'rotate(-40deg)',
              opacity: '50%'
            }}
          />
        </IconButton>
      </Box>
      {rateTableDocument && (
        <TwDocumentViewer
          readableId={undefined}
          selectedFileId={selectedDocumentId}
          open={docViewOpen}
          files={rateTableDocument ? ([rateTableDocument] as AttachmentFile[]) : []}
          onClickClose={function (): void {
            setDocViewOpen(false);
          }}
        />
      )}
    </Box>
  );
};

export const TwEmptyRateTableSelector: React.FC<{
  customerId?: string;
}> = ({ customerId }) => {
  let message =
    'No active rate tables found for this customer, please create one or select another customer';

  if (!customerId) {
    message = 'To apply a rate table to a load, select a customer first';
  }

  return (
    <TwTooltip title={message} placement="bottom">
      <Box sx={{ width: '100%' }}>
        <TwAutocomplete
          name="rate_table"
          label="Rate table"
          optionList={[]}
          value={undefined}
          onChange={(): void => {
            // Do nothing
          }}
          disabled={true}
        />
      </Box>
    </TwTooltip>
  );
};
